// pages/app/Chat.tsx — agent-driven chat: verify memory + ingest from conversation (spec §6.5).
// Persisted + reloaded across visits, paginated (latest page first, older on scroll-up).
import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type UIEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import { useServices } from '../../app/AppContext';
import type { ChatMessage } from '../../lib/api';
import { SparkleIcon } from '../../components/icons';

const PAGE = 50;

const SUGGESTIONS = [
  'What do you know about me?',
  'How do I approach design reviews?',
  'Summarize my working style',
];

/** Local id for optimistic messages that have not been reloaded from the server yet. */
let tmpSeq = 0;
const tmpId = () => `tmp-${++tmpSeq}`;

/** Compact time label; empty for unparseable timestamps. */
export function formatChatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** Animated "typing" dots shown while the agent is composing a reply. */
function TypingDots({ className = '' }: { className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1 ${className}`} aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-current"
          style={{ animationDelay: `${i * 150}ms` }}
        />
      ))}
    </span>
  );
}

export function Chat() {
  const { api } = useServices();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Messages typed while a reply is in flight — auto-sent one at a time when the agent is free.
  const [queue, setQueue] = useState<string[]>([]);

  const startedRef = useRef(false);
  const logRef = useRef<HTMLDivElement>(null);
  // When set, the next layout pass restores scroll position after a prepend (older page).
  const prependHeightRef = useRef<number | null>(null);
  // When true, the next layout pass sticks the view to the newest message.
  const stickRef = useRef(false);
  // Guards against concurrent delivery while draining the queue.
  const deliveringRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const init = await api.chatInit({ limit: PAGE });
        setSessionId(init.sessionId);
        setMessages(init.messages);
        setHasMore(init.hasMore);
        stickRef.current = true;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to start chat');
      } finally {
        setInitializing(false);
      }
    })();
  }, [api]);

  // Keep the scroll anchored: restore position after prepends, otherwise stick to bottom.
  useLayoutEffect(() => {
    const el = logRef.current;
    if (el && prependHeightRef.current != null) {
      el.scrollTop = el.scrollHeight - prependHeightRef.current;
      prependHeightRef.current = null;
    } else if (el && stickRef.current) {
      el.scrollTop = el.scrollHeight;
      stickRef.current = false;
    }
  }, [messages]);

  // Reveal the "thinking" indicator when the agent starts replying.
  useEffect(() => {
    if (busy && logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [busy]);

  // Drain queued messages one at a time, once the agent finishes the current reply.
  useEffect(() => {
    if (busy || deliveringRef.current || queue.length === 0 || !sessionId) return;
    const [next, ...rest] = queue;
    setQueue(rest);
    void deliver(sessionId, next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, queue, sessionId]);

  async function loadOlder() {
    if (!sessionId || !hasMore || loadingOlder || messages.length === 0) return;
    setLoadingOlder(true);
    setError(null);
    prependHeightRef.current = logRef.current?.scrollHeight ?? 0;
    try {
      const page = await api.listChatMessages(sessionId, {
        limit: PAGE,
        before: messages[0].createdAt,
      });
      setMessages((m) => [...page.messages, ...m]);
      setHasMore(page.hasMore);
    } catch (e) {
      prependHeightRef.current = null;
      setError(e instanceof Error ? e.message : 'Failed to load older messages');
    } finally {
      setLoadingOlder(false);
    }
  }

  function onScroll(e: UIEvent<HTMLDivElement>) {
    if (e.currentTarget.scrollTop <= 48) void loadOlder();
  }

  // Deliver one message to the agent and append its reply.
  async function deliver(sid: string, content: string) {
    deliveringRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const res = await api.chatMessage({ sessionId: sid, content });
      stickRef.current = true;
      setMessages((m) => [
        ...m,
        { id: tmpId(), role: 'agent', content: res.reply, createdAt: new Date().toISOString() },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send');
    } finally {
      setBusy(false);
      deliveringRef.current = false;
    }
  }

  function send(e: FormEvent) {
    e.preventDefault();
    if (!sessionId || !input.trim()) return;
    const content = input.trim();
    setInput('');
    stickRef.current = true;
    setMessages((m) => [
      ...m,
      { id: tmpId(), role: 'owner', content, createdAt: new Date().toISOString() },
    ]);
    // If a reply is already streaming, queue this one; the drain effect sends it next.
    if (deliveringRef.current) {
      setQueue((q) => [...q, content]);
    } else {
      void deliver(sessionId, content);
    }
  }

  const showEmpty = !initializing && messages.length === 0;

  return (
    <div className="mx-auto flex h-[calc(100vh-9rem)] max-w-5xl flex-col animate-fade-in">
      <div
        ref={logRef}
        onScroll={onScroll}
        className="flex-1 space-y-4 overflow-y-auto rounded-2xl border border-line/10 bg-surface p-4 sm:p-6"
        data-testid="chat-log"
      >
        {loadingOlder ? (
          <p className="text-center text-xs text-fg-muted" role="status">
            Loading earlier messages…
          </p>
        ) : null}

        {showEmpty ? (
          <div className="flex h-full flex-col items-center justify-center px-6 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 text-brand">
              <SparkleIcon />
            </div>
            <h2 className="mt-4 text-lg font-semibold">Talk to your working self</h2>
            <p className="mt-1 max-w-sm text-sm text-fg-muted">
              Ask what your twin knows, or teach it more. Every turn can grow your memory.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setInput(s)}
                  className="rounded-full border border-line/10 bg-surface-2 px-3 py-1.5 text-xs text-fg-muted transition-colors hover:border-brand/30 hover:text-fg"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m) => {
            const time = formatChatTime(m.createdAt);
            return (
              <div
                key={m.id}
                className={`flex items-end gap-2.5 ${m.role === 'owner' ? 'justify-end' : 'justify-start'}`}
              >
                {m.role === 'agent' ? (
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
                    <SparkleIcon className="h-4 w-4" />
                  </div>
                ) : null}
                <div
                  className={`flex max-w-[80%] flex-col ${m.role === 'owner' ? 'items-end' : 'items-start'}`}
                >
                  <div
                    className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === 'owner'
                        ? 'rounded-br-md bg-brand text-brand-fg'
                        : 'rounded-bl-md bg-surface-2 text-fg'
                    }`}
                  >
                    {m.role === 'agent' ? (
                      <div className="chat-md">
                        <ReactMarkdown>{m.content}</ReactMarkdown>
                      </div>
                    ) : (
                      m.content
                    )}
                  </div>
                  {time ? (
                    <time dateTime={m.createdAt} className="mt-1 px-1 text-[11px] text-fg-muted">
                      {time}
                    </time>
                  ) : null}
                </div>
              </div>
            );
          })
        )}

        {busy ? (
          <div className="flex items-end gap-2.5" role="status" aria-label="Responding">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
              <SparkleIcon className="h-4 w-4" />
            </div>
            <div className="rounded-2xl rounded-bl-md bg-surface-2 px-4 py-3 text-fg-muted">
              <TypingDots />
            </div>
          </div>
        ) : null}
      </div>

      {error ? (
        <p role="alert" className="mt-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      <form className="mt-3 flex items-center gap-2" onSubmit={send}>
        <input
          className="input flex-1"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={busy ? 'Your twin is responding — type to queue…' : 'Message your twin…'}
          aria-label="Message"
          disabled={!sessionId || initializing}
        />
        <button
          type="submit"
          className="btn min-w-[4.5rem] justify-center"
          disabled={!sessionId || initializing || !input.trim()}
        >
          Send
        </button>
      </form>
    </div>
  );
}
